import React, { useState, useEffect, ReactElement, ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import classNames from 'classnames';
import { Icon, IconsMap } from '@nx-frontend/shared/ui/icon';
import { UserPreview } from '@nx-frontend/shared/ui/user-preview';

export interface MultiSelectItem {
  avatarUrl?: string;
  avatarBgClass?: string;
  name: string;
  checked: boolean;
  globalId: string;
  additionalContent?: string | ReactNode;
  separator?: boolean;
  icon?: ReactElement;
}

export enum MultiSelectViewType {
  DEFAULT = 'default',
  AVATARS = 'avatars',
}

interface MultiSelectProps {
  title?: string;
  items: MultiSelectItem[];
  viewType: MultiSelectViewType;
  onChange: (id: string) => void;
  search?: boolean;
  listClass?: string;
}

export const MultiSelect: React.FC<MultiSelectProps> = ({ title, search, items, viewType, onChange, listClass }) => {
  const t = useTranslations('shared');
  const [filteredItems, setFilteredItems] = useState<MultiSelectItem[]>(items);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    setFilteredItems(items.filter((item) => item.name.toLowerCase().includes(searchTerm.toLowerCase())));
  }, [searchTerm, items]);

  const handleChange = (id: string) => {
    onChange(id);
  };
  const handleClear = () => {
    setSearchTerm('');
  };
  return (
    <div className="flex flex-col w-full gap-1.5">
      {search && (
        <div className="relative px-3 py-1.5">
          <input
            className="px-2 py-[5px] border rounded-md focus:outline-none text-neutral-600 leading-5 w-full"
            placeholder={t('multi-select.search_user')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button onClick={handleClear} className="absolute right-4 top-[14px] text-gray-500 hover:text-gray-700">
              <Icon icon={IconsMap.CLOSE} className="w-4 h-4" />
            </button>
          )}
        </div>
      )}
      {title && <div className="px-3 py-1.5 border-b">{title}</div>}
      <ul className={listClass}>
        {filteredItems.map((item) => (
          <li
            key={item.globalId}
            className={classNames(
              'flex justify-between items-center px-3 py-1.5 relative hover:bg-neutral-100 cursor-pointer',
              {
                'border-b border-neutral-200': item.separator,
              }
            )}
            onClick={() => handleChange(item.globalId)}
          >
            {viewType === MultiSelectViewType.AVATARS && (
              <UserPreview
                name={item.name}
                avatarUrl={item.avatarUrl}
                avatarBgClass={item.avatarBgClass}
                icon={item.icon}
              />
            )}
            {viewType === MultiSelectViewType.DEFAULT && (
              <div className="overflow-hidden text-ellipsis whitespace-nowrap">{item.name}</div>
            )}

            {item.additionalContent && <div>{item.additionalContent}</div>}

            {item.checked && (
              <div className="inset-0">
                <Icon icon={IconsMap.CHECK} className="w-[20px] text-teal-900" />
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};
